import { z } from 'zod';
import { PageInfoSchema } from './pagination.js';
/**
 * The three tenant-scoped logs (M11.12, API.md §2.11, FR-ADMIN-11, rules §33).
 *
 * Three logs with three different jobs:
 *
 * - the **audit log** answers "who changed this record, when, and to what";
 * - the **request summary** answers "is anything failing, and where";
 * - **job failures** answer "did the work that happens after a booking actually happen".
 *
 * All three are tenant-scoped **by RLS**, not by a filter this contract carries.
 * Nothing here is a tenant id and nothing may become one: the API resolves the clinic
 * from the host and ignores - and logs - a tenant id anywhere else (API.md §1.4,
 * rules §18.4).
 */
/* --------------------------------------------------------------- audit log ---- */
/** `audit_log.action`, as the clinic screens see it. */
export const AuditActionSchema = z.enum(['insert', 'update', 'delete', 'event']);
/**
 * One field that changed.
 *
 * `redacted: true` means the column holds PHI or a secret and **both** values were
 * replaced - by `audit_redact()` when the row was written, and again by the API on the
 * way out. The row still says *that* the field changed, because "the member id
 * changed" is the auditable fact and the number itself is not (SECURITY.md §7).
 *
 * Values are strings because that is what a diff renders. A number, a boolean or an
 * object is JSON-encoded rather than dropped: "what did it become" with the type
 * quietly removed is a worse answer than a quoted one.
 */
export const AuditFieldChangeSchema = z.object({
    field: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
    redacted: z.boolean(),
});
export const AuditRowSchema = z.object({
    id: z.string(),
    /** ISO 8601. */
    occurredAt: z.string(),
    /** Null for something the platform did on a schedule rather than a person. */
    actorId: z.string().nullable(),
    /** The account's name, or a readable stand-in (`System`, `Deleted account`). */
    actorName: z.string(),
    actorEmail: z.string().nullable(),
    /** Set while a super admin was driving the session (SECURITY.md §6.3). */
    impersonatorId: z.string().nullable(),
    action: AuditActionSchema,
    /** For `event` rows the event name (`auth.mfa.reset`); null for a row change. */
    eventName: z.string().nullable(),
    /** `audit_log.table_name`, e.g. `appointments`. */
    tableName: z.string(),
    /** `audit_log.row_id`. Null for events that are not about one row. */
    rowId: z.string().nullable(),
    /** Empty for `event` rows and for deletes with nothing to diff. */
    changes: z.array(AuditFieldChangeSchema),
    /** The request this happened in, for cross-referencing the request log. */
    correlationId: z.string().nullable(),
});
/**
 * `?actor&actorId&tableName&rowId&action&from&to&limit&cursor`.
 *
 * `actor` is the screen's free-text box, matched against the account's name and email;
 * `actorId` is the exact one a "show everything this person did" link carries.
 */
export const AuditLogQuerySchema = z
    .object({
    actor: z.string().trim().max(120).optional(),
    actorId: z.string().trim().max(128).optional(),
    action: AuditActionSchema.optional(),
    tableName: z.string().trim().max(120).optional(),
    rowId: z.uuid().optional(),
    /** `yyyy-mm-dd`, inclusive, in the clinic's own zone. */
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().min(1).optional(),
})
    .strip();
export const AuditLogResponseSchema = z.object({
    data: z.array(AuditRowSchema),
    page: PageInfoSchema,
});
/** The distinct `table_name` values in this tenant's log, for the filter dropdown. */
export const AuditResourceTypesResponseSchema = z.object({ data: z.array(z.string()) });
/* -------------------------------------------------------- request summary ---- */
export const RequestRouteSummarySchema = z.object({
    /** The route template, never a concrete path: `/admin/appointments/:id`. */
    route: z.string(),
    method: z.string(),
    total: z.number().int().min(0),
    byStatusClass: z.object({
        '2xx': z.number().int().min(0),
        '3xx': z.number().int().min(0),
        '4xx': z.number().int().min(0),
        '5xx': z.number().int().min(0),
    }),
    p50Ms: z.number().int().min(0),
    /** The number an operator can act on; the mean hides the bad minute. */
    p95Ms: z.number().int().min(0),
});
export const RequestLogSummarySchema = z.object({
    /** Echoed, so the screen never states a range it did not get. Both `yyyy-mm-dd`. */
    range: z.object({ from: z.string(), to: z.string(), timeZone: z.string() }),
    totalRequests: z.number().int().min(0),
    /** 4xx and 5xx over total, 0 to 1. Zero when there was no traffic. */
    errorRate: z.number().min(0).max(1),
    p50Ms: z.number().int().min(0),
    p95Ms: z.number().int().min(0),
    /** Busiest routes first, capped. */
    routes: z.array(RequestRouteSummarySchema),
});
export const RequestLogSummaryQuerySchema = z
    .object({
    from: z.iso.date(),
    to: z.iso.date(),
    /** How many routes to return. The tail is a long list of ones. */
    limit: z.coerce.number().int().min(1).max(50).default(20),
})
    .strip();
export const RequestLogSummaryResponseSchema = z.object({ data: RequestLogSummarySchema });
/* ------------------------------------------------------------ job failures ---- */
/** `failed` is a job that exhausted its retries; `dead` is one in a dead-letter queue. */
export const JobFailureStateSchema = z.enum(['failed', 'dead']);
export const JobFailureSchema = z.object({
    id: z.string(),
    queue: z.string(),
    jobName: z.string(),
    /** A redacted one-line summary, safe to render. Never a stack, never a vendor body. */
    errorMessage: z.string(),
    attempts: z.number().int().min(0),
    maxAttempts: z.number().int().min(0),
    /** ISO 8601. */
    createdAt: z.string(),
    lastRunAt: z.string().nullable(),
    state: JobFailureStateSchema,
    /**
     * Whether `POST /admin/logs/jobs/:id/redrive` will do anything for this row.
     *
     * False when the stored payload carries no tenant id - a job the clinic can see
     * because it names their tenant, but whose envelope predates the convention or was
     * written by hand. Re-sending one of those would mean this API choosing a tenant for
     * a payload that does not name one, which is the one thing multi-tenancy must never
     * make routine.
     */
    redrivable: z.boolean(),
});
export const JobFailureQuerySchema = z
    .object({
    queue: z.string().trim().max(60).optional(),
    state: JobFailureStateSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strip();
export const JobFailureListResponseSchema = z.object({ data: z.array(JobFailureSchema) });
//# sourceMappingURL=logs.js.map